import { presenceState } from './staffPresenceModel';

const state = presence => presence?.is_online === true ? 'online' : presence?.is_online === false ? 'offline' : 'unknown';
export function rosterView(data) {
  if (!data) return null;
  return {
    effectivePresence: state(data.self?.wati_presence),
    groups: ['individual_account', 'service_account'].map(mode => ({
      mode: mode === 'service_account' ? 'shared' : 'individual',
      rows: data.staff.filter(row => row.execution_mode === mode).map(row => ({
        userId: row.user_id, displayName: row.display_name,
        srmPresence: state(row.srm_presence), watiPresence: state(row.wati_presence),
        inherited: row.wati_presence_inherited === true,
      })),
    })),
  };
}

export function transitionNotices(response) {
  if (response.baseline !== false || response.data?.notifications_enabled !== true
    || !Array.isArray(response.data?.staff) || !Array.isArray(response.transitions)) return [];
  const grouped = new Map();
  for (const transition of response.transitions) {
    if (typeof transition.from !== 'boolean' || typeof transition.to !== 'boolean'
      || transition.from === transition.to || !['srm', 'wati'].includes(transition.source)) continue;
    const row = response.data.staff.find(item => item.user_id === transition.user_id);
    if (!row) continue;
    // Shared effective WATI availability also changes when a staff member logs
    // in/out. Coalesce that with their SRM change, not a service-account alert.
    const staffChange = transition.source === 'srm' || transition.cause === 'srm_presence';
    const inherited = !staffChange && transition.source === 'wati'
      && transition.inherited === true && transition.cause === 'provider_presence';
    const source = staffChange ? 'srm' : transition.source;
    const presence = inherited ? response.data.service_presence : row[`${source}_presence`];
    // Use authoritative observation time, not client receipt time. Shared WATI
    // observation timestamps come from the same cached provider read in all tabs.
    const at = source === 'wati' ? presence?.observed_at
      : transition.to ? presence?.online_since : presence?.last_seen_at;
    if (!at) continue; // No stable occurrence identity: do not risk duplicate alerts.
    const entity = inherited ? 'service:wati' : `${transition.user_id}:${source}`;
    const key = `${entity}:${transition.from}:${transition.to}:${at}`;
    grouped.set(key, { key, userId: inherited ? null : row.user_id, name: inherited ? 'Shared WATI service account' : row.display_name,
      source: source.toUpperCase(), presence: presenceState(transition.to ? 'online' : 'offline') });
  }
  return [...grouped.values()];
}

// One binding per header/session. No polling; GET bootstraps the view, socket
// snapshots replace it. A late HTTP response never overwrites socket state.
export function bindPresenceContract({ socket, channel, load, publish, error, notify }) {
  let active = true, generation = 0, receivedSocket = false, latestTime = -Infinity, hasSnapshot = false;
  const receive = (response, current, source) => {
    if (!active || current !== generation || (source === 'http' && receivedSocket)) return;
    if (response?.success !== true) {
      if (source === 'socket') receivedSocket = true;
      hasSnapshot = false;
      publish(null);
      error(response?.message || 'Staff presence is unavailable.');
      return;
    }
    const data = response.data;
    if (data?.channel_account_key !== channel || !Array.isArray(data.staff)) return;
    const at = Date.parse(data.observed_at);
    if (!Number.isFinite(at) || at < latestTime) return;
    if (source === 'socket') receivedSocket = true;
    latestTime = at;
    const canAnnounce = hasSnapshot;
    hasSnapshot = true;
    error(null);
    publish(rosterView(data));
    if (source === 'socket' && canAnnounce) transitionNotices(response).forEach(notice => notify?.(notice));
  };
  const onSnapshot = response => { if (socket.connected) receive(response, generation, 'socket'); };
  const reconcile = () => {
    const current = ++generation;
    receivedSocket = false; latestTime = -Infinity; hasSnapshot = false;
    publish(null); error(null);
    socket.emit('messaging:staff_presence:subscribe', { channel_account_key: channel }, response => {
      if (socket.connected) receive(response, current, 'socket');
    });
  };
  const unavailable = () => { generation++; receivedSocket = false; latestTime = -Infinity; hasSnapshot = false; publish(null); };
  socket.on('messaging:staff_presence', onSnapshot);
  socket.on('connect', reconcile);
  socket.on('disconnect', unavailable);
  if (socket.connected) reconcile();
  const httpGeneration = generation;
  Promise.resolve().then(load).then(response => receive(response, httpGeneration, 'http'))
    .catch(() => { if (active && generation === httpGeneration && !receivedSocket) error('Could not load staff presence.'); });
  return () => {
    active = false; generation++;
    socket.off('messaging:staff_presence', onSnapshot);
    socket.off('connect', reconcile);
    socket.off('disconnect', unavailable);
    socket.emit('messaging:staff_presence:unsubscribe');
  };
}

// Web Locks makes the same-origin multi-tab claim atomic. Fail closed when
// locks/storage are unavailable: roster updates still work, only alerts stop.
export async function claimPresenceNotice(scope, key) {
  if (!navigator.locks?.request) return false;
  try {
    return await navigator.locks.request(`staff-presence:${scope}`, () => {
      const storageKey = `uzdpart360:presence-notices:${scope}`;
      const previous = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(previous) || previous.includes(key)) return false;
      localStorage.setItem(storageKey, JSON.stringify([...previous.slice(-99), key]));
      return true;
    });
  } catch { return false; }
}
